import React, { useState, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Visibility as ViewIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@material-ui/icons'
import { Button, Box } from '@mui/material'
import dayjs from 'dayjs'
import {
  AdminPageWrapper,
  AdminSearchBar,
  AdminTable,
  AdminTableColumn,
  SearchFilters,
} from '../AdminComponents'
import { Cohort } from '../../../../Generated/graphql-types'
import SEARCH_COHORTS from '../../../../Apollo/Queries/SearchCohorts'

// Local interface for Cohort Admin search filters
interface CohortSearchFilters {
  id?: string
  name?: string
  funder?: string
  nonprofit?: string
}

const CohortAdmin: React.FC = () => {
  const history = useHistory()
  const location = useLocation()

  // Get filters from URL
  const params = new URLSearchParams(location.search)
  const funderNameFromUrl = params.get('funderName')
  const nonprofitNameFromUrl = params.get('nonprofitName')

  // Initialize search filters with URL params if present
  const [searchFilters, setSearchFilters] = useState<CohortSearchFilters>(
    () => {
      const filters: CohortSearchFilters = {}
      if (funderNameFromUrl) {
        filters.funder = funderNameFromUrl
      }
      if (nonprofitNameFromUrl) {
        filters.nonprofit = nonprofitNameFromUrl
      }
      return filters
    }
  )

  // State for pagination
  const [currentPage, setCurrentPage] = useState(0)
  const pageSize = 20

  // Build query variables
  const queryVariables = useMemo(
    () => ({
      id: searchFilters.id ? parseInt(searchFilters.id, 10) : undefined,
      name: searchFilters.name || undefined,
      funderName: searchFilters.funder || undefined,
      nonprofitName: searchFilters.nonprofit || undefined,
      limit: pageSize,
      offset: currentPage * pageSize,
    }),
    [searchFilters, currentPage]
  )

  const { data, loading, error } = useQuery<{
    searchCohorts: { total: number; cohorts: Cohort[] }
  }>(SEARCH_COHORTS, {
    variables: queryVariables,
  })

  const searchFields = [
    {
      key: 'id',
      label: 'Cohort ID',
      placeholder: 'Enter cohort ID',
      type: 'number' as const,
    },
    { key: 'name', label: 'Cohort Name', placeholder: 'Enter cohort name' },
    {
      key: 'funder',
      label: 'Funder Name',
      placeholder: 'Enter funder name',
    },
    {
      key: 'nonprofit',
      label: 'Nonprofit Name',
      placeholder: 'Enter nonprofit name',
    },
  ]

  const columns: AdminTableColumn<Cohort>[] = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => `#${value}`,
    },
    {
      key: 'name',
      label: 'Name',
      render: (value) => value || 'Unnamed Cohort',
    },
    {
      key: 'startDate',
      label: 'Start',
      render: (value) => (value ? dayjs(value).format('MM/DD/YYYY') : '-'),
      sortable: true,
    },
    {
      key: 'endDate',
      label: 'End',
      render: (value) => (value ? dayjs(value).format('MM/DD/YYYY') : '-'),
      sortable: true,
    },
    {
      key: 'cohortOwnerships',
      label: 'Funders',
      render: (value) => {
        const ownerships = value as Array<unknown> | null | undefined
        return ownerships?.length || 0
      },
      align: 'center' as const,
    },
    {
      key: 'cohortMemberships',
      label: 'Nonprofits',
      render: (value) => {
        const memberships = value as Array<unknown> | null | undefined
        return memberships?.length || 0
      },
      align: 'center' as const,
    },
  ]

  const cohorts = useMemo(
    () => data?.searchCohorts?.cohorts?.filter(Boolean) || [],
    [data?.searchCohorts?.cohorts]
  )

  const totalCount = data?.searchCohorts?.total || 0

  const handleRowClick = (cohort: Cohort) => {
    history.push(`/internal/self-serve/cohort-admin/${cohort.id}`)
  }

  const handleSearch = React.useCallback((filters: CohortSearchFilters) => {
    setSearchFilters(filters)
    setCurrentPage(0) // Reset to first page when searching
  }, [])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const clearUrlFilters = () => {
    history.push('/internal/self-serve/cohort-admin')
    setSearchFilters((prev) => {
      const newFilters = { ...prev }
      delete newFilters.funder
      delete newFilters.nonprofit
      return newFilters
    })
    setCurrentPage(0)
  }

  if (error) {
    return (
      <AdminPageWrapper title='Cohort Administration'>
        <div>Error loading cohorts: {error.message}</div>
      </AdminPageWrapper>
    )
  }

  const getSubtitle = () => {
    if (funderNameFromUrl && nonprofitNameFromUrl) {
      return `Viewing cohorts for funder: ${funderNameFromUrl} and nonprofit: ${nonprofitNameFromUrl}`
    }
    if (funderNameFromUrl) {
      return `Viewing cohorts for funder: ${funderNameFromUrl}`
    }
    if (nonprofitNameFromUrl) {
      return `Viewing cohorts associated with nonprofit: ${nonprofitNameFromUrl}`
    }
    return 'View and manage all cohorts in the system'
  }

  return (
    <AdminPageWrapper
      title='Cohort Administration'
      subtitle={getSubtitle()}
      actions={
        <Box display='flex' gap={2}>
          <Button
            variant='contained'
            size='small'
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/internal/self-serve')}
            style={{
              backgroundColor: '#6B46C1',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Back to Self Serve
          </Button>
          {(funderNameFromUrl || nonprofitNameFromUrl) && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<ClearIcon />}
              onClick={clearUrlFilters}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      }
    >
      <AdminSearchBar
        fields={searchFields}
        onSearch={handleSearch}
        initialFilters={searchFilters as SearchFilters}
      />

      <AdminTable<Cohort>
        columns={columns}
        data={cohorts}
        loading={loading}
        onRowClick={handleRowClick}
        emptyMessage='No cohorts found'
        pageSize={pageSize}
        totalCount={totalCount}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        serverSidePagination
        actions={[
          {
            icon: <ViewIcon />,
            tooltip: 'View details',
            onClick: handleRowClick,
          },
        ]}
      />
    </AdminPageWrapper>
  )
}

export default CohortAdmin
